/**
 * MainAppLayout.js – app shell: sidebar nav, header, and background per page.
 * Used by App.js to wrap Dashboard, Client Profile, Aged Care, etc.
 *
 * THE MENU (owner, 22–24 Sep 2026): what is on it and in what state is one rule, clientMenu.ts —
 * the client file's name, the profile's steps with what is done, the living options for the goals
 * ticked, and Reports once an advice document exists. This file only draws it. See clientMenu.ts
 * for the decisions behind it, including why there is no lock.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { scrollAppToTop } from '../../utils/scrollAppToTop.ts';
import {
    LayoutDashboard,
    User,
    FileText,
    BookOpen,
    LogOut,
    Menu,
    PanelLeft,
    ShieldCheck,
    ClipboardCheck,
    X,
    Check,
    Building,
    Home,
    Users,
    TrendingDown,
    Trees,
    Coins,
} from 'lucide-react';
import { AgedCareWiseLogo, agedCareWiseMarkUrl } from '../branding/index.tsx';
import type { FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import { buildClientMenu, REPORTS_SECTION_ID, type MenuPlan, type MenuCaseStudy } from './clientMenu.ts';
import { stockPhoto } from '../../assets/stock/index.ts';

/**
 * A cohesive family of calm, serene landscapes (curated + verified visually under the app's
 * 85% white overlay). The backdrop changes on every screen — and on every Client Profile step —
 * to keep the experience tranquil and interesting rather than sterile, while staying on-mood.
 */
const bgUrl = (id: string): string => stockPhoto(id, 1920, 1080);

const SERENE_BACKDROPS = [
    bgUrl('1441974231531-c6227db76b6e'), // 0 forest road
    bgUrl('1506905925346-21bda4d32df4'), // 1 mountain lake
    bgUrl('1559827260-dc66d52bef19'),    // 2 green hills
    bgUrl('1439066615861-d1af74d74000'), // 3 misty lake
    bgUrl('1717964134799-a98f497172a5'), // 4 forest river
    bgUrl('1683041133891-613b76cbebc7'), // 5 mountain lake & trees
    bgUrl('1683041132892-0fe990b3afc3'), // 6 calm dusk lake
    bgUrl('1636893580433-5ac59809bb13'), // 7 green meadow path
    bgUrl('1776894873397-78ce93defb29'), // 8 misty lone tree
    bgUrl('1662189793032-ccb6135a9159'), // 9 autumn lake reflection
    bgUrl('1595885914073-3af381bbee7e'), // 10 green hills & hut
];

// Intentional per-page assignments (any page not listed gets a stable hashed pick below).
const PAGE_BACKDROP: Record<string, string> = {
    'Dashboard': SERENE_BACKDROPS[1],
    'Reports': SERENE_BACKDROPS[9],
    'Partners': SERENE_BACKDROPS[10],
    'AssumptionsPage': SERENE_BACKDROPS[5],
    'Assumptions': SERENE_BACKDROPS[5],
    'Aged Care Funding Analyser': SERENE_BACKDROPS[2],
    'Understanding Aged Care Costs': SERENE_BACKDROPS[3],
    'Manual Control Projections': SERENE_BACKDROPS[6],
    'Enhanced Suggestion Engine': SERENE_BACKDROPS[7],
};

// One backdrop per Client Profile step (1–8), so stepping through the form keeps changing the scene.
const CLIENT_PROFILE_STEP_BACKDROPS = [
    SERENE_BACKDROPS[3],  // 1 Who's Completing
    SERENE_BACKDROPS[0],  // 2 Your Details
    SERENE_BACKDROPS[4],  // 3 Goals
    SERENE_BACKDROPS[10], // 4 Living Arrangements
    SERENE_BACKDROPS[5],  // 5 Assets
    SERENE_BACKDROPS[6],  // 6 Liabilities
    SERENE_BACKDROPS[7],  // 7 Income/Expenses
    SERENE_BACKDROPS[8],  // 8 Family/Estate
];

function getBackgroundImage(activePage: string, clientProfileStep: number | string | undefined): string {
    if (activePage === 'Client Profile') {
        const i = (Number(clientProfileStep) || 1) - 1;
        return CLIENT_PROFILE_STEP_BACKDROPS[i] || CLIENT_PROFILE_STEP_BACKDROPS[0];
    }
    if (PAGE_BACKDROP[activePage]) return PAGE_BACKDROP[activePage];
    // Stable, distinct backdrop for any other screen so it still changes on navigation.
    let h = 0;
    const key = String(activePage || '');
    for (let k = 0; k < key.length; k += 1) h = (h * 31 + key.charCodeAt(k)) >>> 0;
    return SERENE_BACKDROPS[h % SERENE_BACKDROPS.length];
}

// Matches Tailwind's `lg` breakpoint — the width at which the 16rem sidebar can sit beside the
// content without squeezing it. Below this the same nav is presented as a hamburger drawer.
const DESKTOP_QUERY = '(min-width: 1024px)';

/** True when the sidebar is docked (desktop), false when it collapses into the hamburger drawer. */
function useIsDesktop(): boolean {
    const [isDesktop, setIsDesktop] = useState(
        () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
            ? window.matchMedia(DESKTOP_QUERY).matches
            : true,
    );

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
        const mql = window.matchMedia(DESKTOP_QUERY);
        const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
        setIsDesktop(mql.matches);
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, []);

    return isDesktop;
}

type IconComponent = React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>;

/** One icon per living option, keyed by the plan type clientMenu.ts names. */
const OPTION_ICONS: Record<string, IconComponent> = {
    aged_care: Building,
    home_care: Home,
    retirement_village: Users,
    downsizer: TrendingDown,
    granny_flat: Trees,
    cashflow_pension: Coins,
};

interface SidebarProps {
    activePage: string;
    setActivePage: (page: string) => void;
    clientProfileStep?: number;
    setClientProfileStep?: (step: number) => void;
    showReturnToPartnerPortal?: boolean;
    onReturnToPartnerPortal?: () => void;
    onSignOut?: () => void;
    onOpenAccount?: () => void;
    onOpenAdmin?: () => void;
    showAccount?: boolean;
    showAdmin?: boolean;
    /** Licensee oversight entry: shown to compliance reviewers (advice under our licence). */
    showCompliance?: boolean;
    onOpenCompliance?: () => void;
    /** What the menu reads (clientMenu.ts): the file, what has been saved, and whose case this is. */
    factFind?: FactFind | null;
    savedStrategies?: readonly MenuPlan[];
    savedCaseStudies?: readonly MenuCaseStudy[];
    professionalCaseId?: string | null;
    /** The Partner Portal's name for the client, used until the profile names them. */
    clientFileFallbackName?: string | null;
}

interface SidebarDrawerProps extends SidebarProps {
    /** Desktop: always docked. Mobile: whether the hamburger drawer is showing. */
    isOpen: boolean;
    isDesktop: boolean;
    onClose: () => void;
}

/** A small count or state beside a menu line: "2 plans", "1 draft", "Complete". */
const Tag = ({ children, tone }: { children: React.ReactNode; tone: 'blue' | 'grey' | 'green' }) => (
    <span
        className={`ml-auto shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold leading-4 ${
            tone === 'blue' ? 'bg-blue-100 text-blue-800' : tone === 'green' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'
        }`}
    >
        {children}
    </span>
);

const GroupHeading = ({ children }: { children: React.ReactNode }) => (
    <p className="px-5 pt-4 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">{children}</p>
);

const Hint = ({ children }: { children: React.ReactNode }) => (
    <p className="mx-3 mb-1 px-4 text-[11px] leading-snug text-slate-400">{children}</p>
);

const Sidebar = ({
    activePage,
    setActivePage,
    clientProfileStep = 1,
    setClientProfileStep,
    showReturnToPartnerPortal,
    onReturnToPartnerPortal,
    onSignOut,
    onOpenAccount,
    onOpenAdmin,
    showAccount,
    showAdmin,
    showCompliance,
    onOpenCompliance,
    factFind,
    savedStrategies,
    savedCaseStudies,
    professionalCaseId,
    clientFileFallbackName,
    isOpen,
    isDesktop,
    onClose,
}: SidebarDrawerProps) => {
    const [navMarkFailed, setNavMarkFailed] = useState(false);
    const closeButtonRef = useRef<HTMLButtonElement | null>(null);

    const menu = useMemo(
        () => buildClientMenu({ factFind, savedStrategies, savedCaseStudies, professionalCaseId, fallbackName: clientFileFallbackName }),
        [factFind, savedStrategies, savedCaseStudies, professionalCaseId, clientFileFallbackName],
    );

    // Drawer mode only: Escape closes, and opening moves focus into the panel so keyboard and
    // screen-reader users land on the nav rather than being left behind on the page.
    useEffect(() => {
        if (isDesktop || !isOpen) return undefined;
        closeButtonRef.current?.focus();
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isDesktop, isOpen, onClose]);

    // Picking anything in the drawer means you're going somewhere — get the drawer out of the way.
    const dismissAfterNavigation = () => {
        if (!isDesktop) onClose();
    };

    /*
     * Land at the top on every page change. "Finish & View Dashboard" used to drop the reader
     * partway down the new page, because nothing reset the scroll position when the page swapped
     * (reported 2026-08-22 — the Dashboard opened at the bottom). Instant rather than smooth: the
     * old page is already gone, so animating a scroll on content the reader never saw is just a
     * delay before they can read the new page.
     */
    useEffect(() => {
        scrollAppToTop('auto');
    }, [activePage]);

    const onProfilePage = activePage === 'Client Profile';
    // The steps stay open while the profile is being filled in, from any page; once it is complete
    // they fold away behind the tag, unless the reader is on the profile itself.
    const showSteps = onProfilePage || !menu.profileComplete;

    const goTo = (page: string) => {
        setActivePage(page);
        dismissAfterNavigation();
    };

    const goToStep = (step: number) => {
        setClientProfileStep?.(step);
        if (!onProfilePage) setActivePage('Client Profile');
        dismissAfterNavigation();
    };

    /**
     * Reports opens the advice documents where they live — the Dashboard's "SOA Generated
     * Scenarios" section — rather than a page of its own that would say "coming soon". The page
     * change scrolls to the top first (the effect above), so the scroll to the section follows it.
     */
    const goToReports = () => {
        const scroll = () => document.getElementById(REPORTS_SECTION_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (activePage === 'Dashboard') scroll();
        else {
            setActivePage('Dashboard');
            window.setTimeout(scroll, 150);
        }
        dismissAfterNavigation();
    };

    const navLinkBase =
        'group relative flex items-center w-full py-2.5 rounded-lg transition-colors duration-200';
    const navLinkActive =
        'bg-blue-50 text-[#001E7A] font-semibold shadow-sm before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[3px] before:rounded-r before:bg-[#0F52E0]';
    const navLinkInactive =
        'text-slate-600 hover:bg-slate-50 hover:text-slate-900';
    const footLink =
        'w-full flex items-center gap-3 px-3 py-2.5 text-left text-slate-600 hover:bg-slate-50 hover:text-[#001E7A] rounded-lg transition-colors duration-200';

    // Off-canvas below `lg`, docked from `lg` up. When the drawer is shut its links are hidden from
    // assistive tech and taken out of the tab order, so tabbing can't wander into an invisible menu.
    const isHiddenDrawer = !isDesktop && !isOpen;

    const goToDashboard = () => goTo('Dashboard');

    return (
        <nav
            id="app-sidebar"
            aria-label="Main"
            aria-hidden={isHiddenDrawer || undefined}
            inert={isHiddenDrawer || undefined}
            className={`fixed inset-y-0 left-0 z-40 w-64 max-w-[85vw] shrink-0 flex flex-col overflow-y-auto py-5 bg-white border-r border-slate-200/90 shadow-2xl transition-transform duration-300 ease-in-out lg:relative lg:z-10 lg:max-w-none lg:translate-x-0 lg:shadow-sm ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}
        >
            <button
                type="button"
                ref={closeButtonRef}
                onClick={onClose}
                aria-label="Close menu"
                className="absolute top-3 right-3 p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors duration-200 lg:hidden"
            >
                <X size={20} aria-hidden />
            </button>
            <div
                className="w-20 h-20 mb-5 cursor-pointer mx-auto"
                onClick={goToDashboard}
                role="button"
                tabIndex={0}
                aria-label="AgedCareWise — go to Dashboard"
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        goToDashboard();
                    }
                }}
            >
                {navMarkFailed ? (
                    <svg width="96" height="96" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-full w-full" aria-hidden>
                        <path d="M35.25 15.8333C37.8864 19.539 38.3333 24.1973 36.4167 28.3333C34.5 32.4694 30.4606 35.4167 25.8333 36.4167C21.2061 37.4167 16.4223 36.3125 12.75 33.5C9.07767 30.6875 6.94491 26.4924 6.94491 21.9444C6.94491 17.3965 9.07767 13.2014 12.75 10.3889C16.4223 7.57639 21.2061 6.47222 25.8333 7.47222" stroke="#2B6CB0" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="21.6667" cy="18.3333" r="5" fill="#3B82F6"/>
                        <path d="M15 30C15 26.3181 18.134 23.3333 22.0833 23.3333C26.0326 23.3333 29.1667 26.3181 29.1667 30H15Z" fill="#3B82F6"/>
                    </svg>
                ) : (
                    <img
                        src={agedCareWiseMarkUrl}
                        alt=""
                        width={96}
                        height={96}
                        className="h-full w-full object-contain object-center p-1 select-none"
                        draggable={false}
                        decoding="async"
                        onError={() => setNavMarkFailed(true)}
                    />
                )}
            </div>

            {/* Whose file this is — the name and nothing else (owner, 23 Sep 2026). */}
            <div className="mx-3 mb-3 rounded-[10px] border border-slate-200 bg-slate-50 px-3 py-2.5" data-testid="client-file">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Client file</p>
                <p className={`mt-0.5 text-sm font-bold leading-snug ${menu.fileName ? 'text-slate-900' : 'text-slate-400 font-semibold'}`}>
                    {menu.fileName ?? 'Not yet named'}
                </p>
            </div>

            <ul className="space-y-1 px-3">
                <li>
                    <a
                        href="#"
                        onClick={(e) => { e.preventDefault(); goToDashboard(); }}
                        className={`${navLinkBase} pl-4 pr-3 ${activePage === 'Dashboard' ? navLinkActive : navLinkInactive}`}
                    >
                        <LayoutDashboard size={20} className="mr-3 shrink-0 opacity-90" strokeWidth={activePage === 'Dashboard' ? 2.25 : 2} />
                        <span className="text-sm">Dashboard</span>
                    </a>
                </li>
                <li>
                    {/* One highlight: while a step is highlighted below, the parent goes bold only. */}
                    <a
                        href="#"
                        onClick={(e) => { e.preventDefault(); goTo('Client Profile'); }}
                        className={`${navLinkBase} pl-4 pr-3 ${onProfilePage ? 'text-[#001E7A] font-semibold' : navLinkInactive}`}
                    >
                        <User size={20} className="mr-3 shrink-0 opacity-90" strokeWidth={onProfilePage ? 2.25 : 2} />
                        <span className="text-sm">Client Profile</span>
                        {menu.profileComplete && <Tag tone="green">Complete</Tag>}
                    </a>
                    {showSteps && (
                        <div className="mt-2 mb-1 pl-3 pr-1">
                            <ul className="space-y-0.5 border-l border-slate-200 ml-2 pl-2">
                                {menu.steps.map((s) => {
                                    const subActive = onProfilePage && clientProfileStep === s.step;
                                    return (
                                        <li key={s.step}>
                                            <a
                                                href="#"
                                                onClick={(e) => { e.preventDefault(); goToStep(s.step); }}
                                                className={`flex items-start gap-2 py-1.5 pl-2 pr-2 rounded-md text-xs transition-colors duration-200 ${subActive ? 'bg-blue-50 text-[#001E7A] font-semibold' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                                            >
                                                <span
                                                    className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                                                        subActive ? 'bg-[#0F52E0] text-white' : s.done ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                                                    }`}
                                                    aria-hidden
                                                >
                                                    {s.done && !subActive ? <Check size={11} strokeWidth={3} /> : s.step}
                                                </span>
                                                <span className="leading-snug pt-0.5">{s.name}</span>
                                            </a>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}
                </li>
            </ul>
            {menu.livingOptions.length === 0 && menu.livingHint && <Hint>{menu.livingHint}</Hint>}

            {menu.livingOptions.length > 0 && (
                <>
                    <GroupHeading>Living options</GroupHeading>
                    <ul className="space-y-1 px-3">
                        {menu.livingOptions.map((o) => {
                            const Icon = OPTION_ICONS[o.planType] ?? Home;
                            const active = activePage === o.page;
                            return (
                                <li key={o.page}>
                                    <a
                                        href="#"
                                        onClick={(e) => { e.preventDefault(); goTo(o.page); }}
                                        className={`${navLinkBase} pl-4 pr-3 ${active ? navLinkActive : navLinkInactive}`}
                                    >
                                        <Icon size={20} className="mr-3 shrink-0 opacity-90" strokeWidth={active ? 2.25 : 2} />
                                        <span className="text-sm">{o.label}</span>
                                        {o.planTag && <Tag tone="blue">{o.planTag}</Tag>}
                                    </a>
                                </li>
                            );
                        })}
                    </ul>
                    {menu.livingHint && <Hint>{menu.livingHint}</Hint>}
                </>
            )}

            {menu.reports && (
                <>
                    <GroupHeading>Documents</GroupHeading>
                    <ul className="space-y-1 px-3">
                        <li>
                            <a
                                href="#"
                                onClick={(e) => { e.preventDefault(); goToReports(); }}
                                className={`${navLinkBase} pl-4 pr-3 ${navLinkInactive}`}
                            >
                                <FileText size={20} className="mr-3 shrink-0 opacity-90" strokeWidth={2} />
                                <span className="text-sm">Reports</span>
                                <Tag tone="grey">{menu.reports.tag}</Tag>
                            </a>
                        </li>
                    </ul>
                </>
            )}

            {/* Bottom cluster: reference and account — Assumptions is important and stays a click
                away, but it is not somewhere a client goes to do something, so it sits here rather
                than between the profile and the plans. One nav language, no floating pills. */}
            <div className="mt-auto px-3 pt-4 border-t border-slate-200/80 space-y-0.5">
                <button
                    type="button"
                    onClick={() => goTo('AssumptionsPage')}
                    className={`${footLink} ${activePage === 'AssumptionsPage' ? 'bg-blue-50 text-[#001E7A] font-semibold' : ''}`}
                >
                    <BookOpen size={20} className="flex-shrink-0" aria-hidden />
                    <span className="text-sm font-medium">Assumptions</span>
                </button>
                {showReturnToPartnerPortal && onReturnToPartnerPortal && (
                    <button
                        type="button"
                        onClick={() => { onReturnToPartnerPortal(); dismissAfterNavigation(); }}
                        aria-label="Return to Partner Portal"
                        className={footLink}
                    >
                        <PanelLeft size={20} className="flex-shrink-0" aria-hidden />
                        <span className="text-sm font-medium">Return to Portal</span>
                    </button>
                )}
                {showAccount && onOpenAccount && (
                    <button
                        type="button"
                        onClick={() => { onOpenAccount(); dismissAfterNavigation(); }}
                        className={footLink}
                    >
                        <User size={20} className="flex-shrink-0" aria-hidden />
                        <span className="text-sm font-medium">Account</span>
                    </button>
                )}
                {showAdmin && onOpenAdmin && (
                    <button
                        type="button"
                        onClick={() => { onOpenAdmin(); dismissAfterNavigation(); }}
                        className={footLink}
                    >
                        <ShieldCheck size={20} className="flex-shrink-0" aria-hidden />
                        <span className="text-sm font-medium">Admin</span>
                    </button>
                )}
                {showCompliance && onOpenCompliance && (
                    <button
                        type="button"
                        onClick={() => { onOpenCompliance(); dismissAfterNavigation(); }}
                        className={footLink}
                    >
                        <ClipboardCheck size={20} className="flex-shrink-0" aria-hidden />
                        <span className="text-sm font-medium">Our advice</span>
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => {
                        // A REAL sign-out must end the auth session and reset app state — App.js owns
                        // that (handleSignedOut). The old storage-only wipe left the session alive and
                        // the previous user's data on screen for the next person (shared computers).
                        if (onSignOut) {
                            onSignOut();
                            return;
                        }
                        // Demo builds (no accounts wired): keep the original local wipe.
                        sessionStorage.clear();
                        localStorage.clear();
                        setActivePage('LandingPage');
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-slate-600 hover:bg-red-50 hover:text-red-700 rounded-lg transition-colors duration-200"
                >
                    <LogOut size={20} />
                    <span className="text-sm font-medium">Sign Out</span>
                </button>
            </div>
        </nav>
    );
};

interface HeaderProps {
    onOpenMenu: () => void;
    menuOpen: boolean;
}

const Header = ({ onOpenMenu, menuOpen }: HeaderProps) => (
    <header className="flex items-center gap-3 p-4 sm:p-6 lg:p-8">
        <button
            type="button"
            onClick={onOpenMenu}
            aria-label="Open menu"
            aria-controls="app-sidebar"
            aria-expanded={menuOpen}
            className="shrink-0 p-2 -ml-1 rounded-lg bg-white/80 text-slate-700 border border-slate-200/90 shadow-sm hover:bg-white hover:text-[#001E7A] transition-colors duration-200 lg:hidden"
        >
            <Menu size={24} aria-hidden />
        </button>
        <AgedCareWiseLogo className="h-14 w-auto sm:h-20" />
    </header>
);

interface MainAppLayoutProps extends SidebarProps {
    children?: React.ReactNode;
}

const MainAppLayout = ({
    activePage,
    setActivePage,
    children,
    clientProfileStep,
    setClientProfileStep,
    showReturnToPartnerPortal,
    onReturnToPartnerPortal,
    onSignOut,
    onOpenAccount,
    onOpenAdmin,
    showAccount,
    showAdmin,
    showCompliance,
    onOpenCompliance,
    factFind,
    savedStrategies,
    savedCaseStudies,
    professionalCaseId,
    clientFileFallbackName,
}: MainAppLayoutProps) => {
    const isDesktop = useIsDesktop();
    const [menuOpen, setMenuOpen] = useState(false);
    const closeMenu = React.useCallback(() => setMenuOpen(false), []);

    // Widening back to desktop docks the sidebar again — don't leave a stale "open drawer" state
    // behind that would re-open as an overlay the next time the window narrows.
    useEffect(() => {
        if (isDesktop) setMenuOpen(false);
    }, [isDesktop]);

    return (
    <div
        className="relative flex h-screen font-sans text-gray-800 overflow-hidden"
        style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.85), rgba(255,255,255,0.85)), url("${getBackgroundImage(activePage, clientProfileStep)}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
            backgroundRepeat: 'no-repeat',
        }}
    >
        <div className="absolute top-0 left-0 w-full h-full -z-10 overflow-hidden">
            <div className="absolute top-[-30%] left-[-25%] w-[70rem] h-[70rem] bg-blue-400/25 rounded-full filter blur-3xl animate-blob"></div>
            <div className="absolute bottom-[-30%] right-[-25%] w-[70rem] h-[70rem] bg-teal-400/25 rounded-full filter blur-3xl animate-blob animation-delay-2000"></div>
        </div>
        {/* Scrim: dims the page behind the drawer and gives a tap-anywhere-to-dismiss target. */}
        {menuOpen && !isDesktop && (
            <div
                className="fixed inset-0 z-30 bg-slate-900/40 lg:hidden"
                onClick={closeMenu}
                aria-hidden
            />
        )}
        <Sidebar
            isOpen={isDesktop || menuOpen}
            isDesktop={isDesktop}
            onClose={closeMenu}
            activePage={activePage}
            setActivePage={setActivePage}
            clientProfileStep={clientProfileStep}
            setClientProfileStep={setClientProfileStep}
            showReturnToPartnerPortal={showReturnToPartnerPortal}
            onReturnToPartnerPortal={onReturnToPartnerPortal}
            onSignOut={onSignOut}
            onOpenAccount={onOpenAccount}
            onOpenAdmin={onOpenAdmin}
            showAccount={showAccount}
            showAdmin={showAdmin}
            showCompliance={showCompliance}
            onOpenCompliance={onOpenCompliance}
            factFind={factFind}
            savedStrategies={savedStrategies}
            savedCaseStudies={savedCaseStudies}
            professionalCaseId={professionalCaseId}
            clientFileFallbackName={clientFileFallbackName}
        />
        {/* The app's one scrolling region. Marked so scrollAppToTop can find IT rather than the
            sidebar, which also carries overflow-y-auto and comes first in the DOM. */}
        <div data-app-scroll-container className="flex-1 min-w-0 flex flex-col overflow-y-auto">
            <Header onOpenMenu={() => setMenuOpen(true)} menuOpen={menuOpen} />
            {/* pb-28 on small screens reserves the strip the floating AWise button occupies
                (App.tsx: fixed bottom-8 right-8, ~56px tall). Without it the button sits on top of
                whatever the page ends with, and on a phone that is the card's own Back/Next row —
                the Next button was unreachable on every step of the client profile (2026-08-19).
                The laptop never showed it: there the card is narrower than the viewport, so the
                button floats clear to its right. */}
            <main className="flex-1 p-4 sm:p-6 lg:p-8 pb-28 xl:pb-8">
                {children}
            </main>
        </div>
    </div>
    );
};

export default MainAppLayout;
